import React, { useEffect, useState, useMemo } from "react";
import {
  Coins,
  TrendingUp,
  AlertTriangle,
  CheckCircle2,
  RefreshCw,
  Save,
  Bell,
  Users,
} from "lucide-react";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip as ReTooltip,
  Legend,
  CartesianGrid,
} from "recharts";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import { Label } from "@/components/ui/label";
import LoadingState from "@/components/common/LoadingState";
import { apiGet, apiPost, apiPut } from "@/lib/api";
import { toast } from "sonner";

const fmtUsd = (n) =>
  `$${Number(n || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const fmtTokens = (n) => {
  const v = Number(n || 0);
  if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1_000) return `${(v / 1_000).toFixed(1)}K`;
  return String(v);
};

// Use Tailwind theme variables (HSL) — keeps the chart on-brand for both
// light and dark mode.
const CATEGORY_COLOR = {
  report_analysis: "hsl(var(--primary))",
  ai_chat: "hsl(var(--info, 200 90% 45%))",
  rationale: "hsl(var(--success))",
  market_pdf: "hsl(40 90% 50%)",
  company_score: "hsl(280 60% 55%)",
  sector_analysis: "hsl(340 70% 50%)",
  // Phase 8 — Deep Analysis breakdown
  deep_analysis_shared: "hsl(188 72% 38%)",
  deep_analysis_personal_context: "hsl(174 60% 40%)",
  deep_analysis_macro_cache: "hsl(220 55% 50%)",
  deep_analysis_geo_cache: "hsl(260 50% 55%)",
  other: "hsl(var(--muted-foreground))",
};

const CATEGORY_LABEL = {
  report_analysis: "Report analysis",
  ai_chat: "AI Chat",
  rationale: "Rationale",
  market_pdf: "Market PDFs",
  company_score: "Score recompute",
  sector_analysis: "Sector analysis",
  deep_analysis_shared: "Deep Analysis · runs",
  deep_analysis_personal_context: "Deep Analysis · personal",
  deep_analysis_macro_cache: "Deep Analysis · macro cache",
  deep_analysis_geo_cache: "Deep Analysis · geo cache",
  other: "Other",
};

const STATUS_TONE = {
  ok: { dot: "bg-success", text: "text-success", label: "Under budget", border: "border-success/40" },
  warn: { dot: "bg-warning", text: "text-warning", label: "Approaching budget", border: "border-warning/40" },
  over: { dot: "bg-destructive", text: "text-destructive", label: "Budget exceeded", border: "border-destructive/40" },
};

export default function AdminApiCostsPage() {
  const [budget, setBudget] = useState(null);
  const [monthly, setMonthly] = useState(null);
  const [topOps, setTopOps] = useState([]);
  const [perUser, setPerUser] = useState([]);
  const [loading, setLoading] = useState(true);

  // Budget edit form state
  const [budgetForm, setBudgetForm] = useState({
    monthly_budget_usd: 30,
    alert_threshold_pct: 80,
    alert_email: "",
  });
  const [savingBudget, setSavingBudget] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [b, m, t, u] = await Promise.all([
        apiGet("/admin/api-costs/budget"),
        apiGet("/admin/api-costs/monthly?months=6"),
        apiGet("/admin/api-costs/top-ops?limit=10&days=30"),
        apiGet("/admin/api-costs/per-user?days=30&limit=50"),
      ]);
      setBudget(b);
      setMonthly(m);
      setTopOps(t.items || []);
      setPerUser(u.items || []);
      if (b?.budget) {
        setBudgetForm({
          monthly_budget_usd: b.budget.monthly_budget_usd ?? 30,
          alert_threshold_pct: b.budget.alert_threshold_pct ?? 80,
          alert_email: b.budget.alert_email ?? "",
        });
      }
    } catch (e) {
      toast.error("Failed to load cost dashboard", { description: e?.message });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const saveBudget = async () => {
    setSavingBudget(true);
    try {
      const payload = {
        monthly_budget_usd: Number(budgetForm.monthly_budget_usd),
        alert_threshold_pct: Number(budgetForm.alert_threshold_pct),
        alert_email: (budgetForm.alert_email || "").trim() || null,
      };
      await apiPut("/admin/api-costs/budget", payload);
      toast.success("Budget settings saved");
      load();
    } catch (e) {
      toast.error("Could not save budget", { description: e?.response?.data?.detail || e.message });
    } finally {
      setSavingBudget(false);
    }
  };

  const fireAlertCheck = async () => {
    try {
      const r = await apiPost("/admin/api-costs/check-alert", {});
      if (r.sent) toast.success("Alert email dispatched");
      else if (r.reason === "under_threshold") toast.info("Spend below alert threshold — no email sent");
      else if (r.reason === "already_alerted_this_month") toast.info("Already alerted this month — suppression in effect");
      else toast.warning("Alert check completed", { description: r.error || "Could not send" });
    } catch (e) {
      toast.error("Alert check failed", { description: e?.response?.data?.detail || e.message });
    }
  };

  const chartData = useMemo(() => {
    if (!monthly?.series) return [];
    return monthly.series.map((row) => {
      const r = { month: row.month };
      Object.entries(row.by_category || {}).forEach(([cat, info]) => {
        r[cat] = info.cost_usd || 0;
      });
      return r;
    });
  }, [monthly]);

  const tone = STATUS_TONE[budget?.status || "ok"];

  if (loading) return <LoadingState label="Loading cost dashboard…" />;

  return (
    <div className="space-y-6 max-w-7xl" data-testid="admin-api-costs-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-display text-2xl font-semibold flex items-center gap-2">
            <Coins className="h-5 w-5 text-primary" /> API Cost Dashboard
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Total Claude / OpenAI / Gemini spend tracked by category. Admin-only —
            never exposed to regular users.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={load} data-testid="api-costs-refresh">
          <RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Refresh
        </Button>
      </div>

      {/* Budget headline + projection */}
      <Card className={`border ${tone.border}`} data-testid="api-costs-budget-card">
        <CardContent className="p-5">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Stat
              label="This month"
              value={fmtUsd(budget?.this_month_usd)}
              sub={`Day ${budget?.days_elapsed} of ${budget?.days_in_month}`}
              testId="api-costs-stat-month"
            />
            <Stat
              label="Projected month"
              value={fmtUsd(budget?.projected_month_usd)}
              sub={`${budget?.pct_projected ?? 0}% of budget`}
              testId="api-costs-stat-projected"
            />
            <Stat
              label="Last month"
              value={fmtUsd(budget?.last_month_usd)}
              sub="Final total"
              testId="api-costs-stat-last-month"
            />
            <Stat
              label="Monthly budget"
              value={fmtUsd(budget?.budget?.monthly_budget_usd)}
              sub={`Alert at ${budget?.budget?.alert_threshold_pct ?? 80}%`}
              testId="api-costs-stat-budget"
            />
          </div>
          <div className="mt-4">
            <div className="flex items-center justify-between mb-1.5 text-xs">
              <div className="flex items-center gap-2">
                <span className={`h-2 w-2 rounded-full ${tone.dot}`} />
                <span className={`font-medium ${tone.text}`} data-testid="api-costs-status">
                  {tone.label}
                </span>
                <span className="text-muted-foreground">
                  · {budget?.pct_used ?? 0}% used
                </span>
              </div>
              <span className="text-muted-foreground tabular-nums">
                {fmtUsd(budget?.this_month_usd)} / {fmtUsd(budget?.budget?.monthly_budget_usd)}
              </span>
            </div>
            <Progress
              value={Math.min(100, Number(budget?.pct_used) || 0)}
              data-testid="api-costs-progress"
            />
          </div>
        </CardContent>
      </Card>

      {/* Monthly chart */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base font-display flex items-center gap-2">
            <TrendingUp className="h-4 w-4 text-primary" />
            Monthly cost by category — last 6 months
          </CardTitle>
          <CardDescription>
            Stacked bar chart aggregated from the <code className="text-[11px] bg-muted px-1 py-0.5 rounded">llm_costs</code> ledger.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {chartData.length === 0 ? (
            <div className="h-72 flex items-center justify-center text-sm text-muted-foreground">
              No spend recorded yet.
            </div>
          ) : (
            <div className="h-72" data-testid="api-costs-chart">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="month" stroke="hsl(var(--muted-foreground))" fontSize={11} />
                  <YAxis
                    stroke="hsl(var(--muted-foreground))"
                    fontSize={11}
                    tickFormatter={(v) => `$${v.toFixed(0)}`}
                  />
                  <ReTooltip
                    contentStyle={{
                      background: "hsl(var(--card))",
                      border: "1px solid hsl(var(--border))",
                      fontSize: 12,
                    }}
                    formatter={(v, name) => [fmtUsd(v), CATEGORY_LABEL[name] || name]}
                  />
                  <Legend
                    formatter={(v) => CATEGORY_LABEL[v] || v}
                    wrapperStyle={{ fontSize: 11 }}
                  />
                  {Object.keys(CATEGORY_COLOR).map((cat) => (
                    <Bar key={cat} dataKey={cat} stackId="a" fill={CATEGORY_COLOR[cat]} />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top expensive operations */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base font-display">
              Top 10 most expensive operations · last 30 days
            </CardTitle>
          </CardHeader>
          <CardContent>
            {topOps.length === 0 ? (
              <div className="text-xs text-muted-foreground italic">No spend recorded yet.</div>
            ) : (
              <div className="overflow-x-auto rounded-md border">
                <table className="w-full text-xs" data-testid="api-costs-top-ops-table">
                  <thead className="bg-muted/40">
                    <tr>
                      <th className="text-left px-3 py-2">Date</th>
                      <th className="text-left px-3 py-2">Type</th>
                      <th className="text-left px-3 py-2">Stock / Sector</th>
                      <th className="text-left px-3 py-2 hidden md:table-cell">Model</th>
                      <th className="text-right px-3 py-2 hidden md:table-cell">Tokens</th>
                      <th className="text-right px-3 py-2">Cost</th>
                    </tr>
                  </thead>
                  <tbody>
                    {topOps.map((op, idx) => (
                      <tr key={op.id || idx} className="border-t" data-testid={`api-costs-top-op-${idx}`}>
                        <td className="px-3 py-1.5 tabular-nums">
                          {op.created_at ? new Date(op.created_at).toLocaleDateString() : "—"}
                        </td>
                        <td className="px-3 py-1.5">{op.category_label || op.category || "—"}</td>
                        <td className="px-3 py-1.5">
                          {op.company_code || op.metadata?.sector || op.metadata?.session_id || "—"}
                        </td>
                        <td className="px-3 py-1.5 text-muted-foreground hidden md:table-cell font-mono text-[10.5px] truncate max-w-[12rem]">
                          {op.model || "—"}
                        </td>
                        <td className="px-3 py-1.5 text-right hidden md:table-cell tabular-nums text-muted-foreground">
                          {fmtTokens(op.input_tokens + op.output_tokens)}
                        </td>
                        <td className="px-3 py-1.5 text-right font-semibold tabular-nums">
                          {fmtUsd(op.cost_usd)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Budget settings */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base font-display flex items-center gap-2">
              <Bell className="h-4 w-4 text-primary" /> Budget & Alert Settings
            </CardTitle>
            <CardDescription>
              An alert email is sent once per month when spend crosses the threshold.
              Falls back to the first admin email if no override is set.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="budget-monthly" className="text-xs">Monthly budget (USD)</Label>
                <Input
                  id="budget-monthly"
                  type="number"
                  min="0.5"
                  step="0.5"
                  value={budgetForm.monthly_budget_usd}
                  onChange={(e) => setBudgetForm({ ...budgetForm, monthly_budget_usd: e.target.value })}
                  data-testid="budget-monthly-input"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="budget-pct" className="text-xs">Alert threshold (%)</Label>
                <Input
                  id="budget-pct"
                  type="number"
                  min="1"
                  max="100"
                  step="5"
                  value={budgetForm.alert_threshold_pct}
                  onChange={(e) => setBudgetForm({ ...budgetForm, alert_threshold_pct: e.target.value })}
                  data-testid="budget-pct-input"
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="budget-email" className="text-xs">
                Alert email (optional — defaults to primary admin)
              </Label>
              <Input
                id="budget-email"
                type="email"
                placeholder="admin@example.com"
                value={budgetForm.alert_email}
                onChange={(e) => setBudgetForm({ ...budgetForm, alert_email: e.target.value })}
                data-testid="budget-email-input"
              />
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <Button onClick={saveBudget} disabled={savingBudget} size="sm" data-testid="budget-save-btn">
                <Save className="h-3.5 w-3.5 mr-1.5" />
                Save settings
              </Button>
              <Button onClick={fireAlertCheck} variant="outline" size="sm" data-testid="budget-test-alert-btn">
                <Bell className="h-3.5 w-3.5 mr-1.5" />
                Run alert check
              </Button>
            </div>
            <div className={`rounded-md border p-3 text-xs ${tone.border}`}>
              <div className="flex items-center gap-2 font-medium">
                {budget?.status === "ok" ? (
                  <CheckCircle2 className={`h-3.5 w-3.5 ${tone.text}`} />
                ) : (
                  <AlertTriangle className={`h-3.5 w-3.5 ${tone.text}`} />
                )}
                <span className={tone.text}>{tone.label}</span>
              </div>
              <p className="text-muted-foreground mt-1 leading-snug">
                {budget?.status === "ok"
                  ? `At current run-rate you'll spend ${fmtUsd(budget?.projected_month_usd)} this month — within your ${fmtUsd(budget?.budget?.monthly_budget_usd)} budget.`
                  : `Projected spend ${fmtUsd(budget?.projected_month_usd)} (${budget?.pct_projected}% of ${fmtUsd(budget?.budget?.monthly_budget_usd)} budget). Review heavy usage categories below.`}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Per-user AI chat cost */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base font-display flex items-center gap-2">
            <Users className="h-4 w-4 text-primary" />
            Per-user AI chat cost · last 30 days
          </CardTitle>
          <CardDescription>
            Helps identify heavy AI Chat users. Admin-only — never visible to users.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {perUser.length === 0 ? (
            <div className="text-xs text-muted-foreground italic">
              No AI chat usage recorded yet.
            </div>
          ) : (
            <div className="overflow-x-auto rounded-md border">
              <table className="w-full text-xs" data-testid="api-costs-per-user-table">
                <thead className="bg-muted/40">
                  <tr>
                    <th className="text-left px-3 py-2">User</th>
                    <th className="text-right px-3 py-2">Chats</th>
                    <th className="text-right px-3 py-2 hidden md:table-cell">Tokens</th>
                    <th className="text-right px-3 py-2">Cost</th>
                  </tr>
                </thead>
                <tbody>
                  {perUser.map((row, idx) => (
                    <tr key={row.user_id || idx} className="border-t" data-testid={`api-costs-user-${idx}`}>
                      <td className="px-3 py-1.5">{row.user_email}</td>
                      <td className="px-3 py-1.5 text-right tabular-nums">{row.calls}</td>
                      <td className="px-3 py-1.5 text-right tabular-nums hidden md:table-cell text-muted-foreground">
                        {fmtTokens(row.input_tokens + row.output_tokens)}
                      </td>
                      <td className="px-3 py-1.5 text-right font-semibold tabular-nums">
                        {fmtUsd(row.cost_usd)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

const Stat = ({ label, value, sub, testId }) => (
  <div data-testid={testId}>
    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
    <div className="text-2xl font-display font-semibold tabular-nums mt-0.5">{value}</div>
    {sub && <div className="text-[11px] text-muted-foreground mt-0.5">{sub}</div>}
  </div>
);
